<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import store from "@/store";

import { toast } from "@/common/utils/toast";

import {
  collectionGalleryList,
  collectionGalleryCreate,
  collectionGalleryPatch,
  collectionGalleryDel,
  collectionGalleryFileList,
  collectionGalleryFileCreate,
  collectionGalleryFileRemove,
} from "@/common/api/rest/collection-gallery";

import type { AvatarType } from "@/common/types/common";
import type { TranslationFieldsType } from "@/common/types/translation";
import type { CollectionType } from "@/common/types/collection";
import type {
  CollectionGalleryType,
  CollectionGalleryPayloadType,
} from "@/common/types/collection-gallery";

import { FormGallery, ListTranslate } from "@/components";

import {
  baseAvatar,
  baseConfirm,
  baseModal,
  baseSidebar,
  baseLoading,
  cropImage,
  svgIcon,
  viewImage,
  viewImageList,
} from "@/common/components";

const { t, locale } = useI18n();

const props = defineProps<{
  collection: CollectionType;
}>();

const ratio = 1.333;
const imageSize = "15rem";

const galleries = ref<CollectionGalleryType[]>([]);
const images = ref<{ [key: string]: AvatarType[] }>({});

const payloadGallery = ref<CollectionGalleryPayloadType | null>(null);
const selectedGallery = ref<CollectionGalleryType | null>(null);
const selectedImage = ref<string | null>(null);
const selectedImageList = ref<string[]>([]);
const selectedImageIndex = ref<number | null>(null);

const loading = ref(false);
const showImage = ref(false);
const showModalAdd = ref(false);
const showModalEdit = ref(false);
const showModalDel = ref(false);
const showModalAddImage = ref(false);
const showModalDelImage = ref(false);

const translationFields = ref<TranslationFieldsType[]>([
  {
    type: "input",
    field: "name",
    label: "common.labels.name",
    value: "",
  },
]);

// files
async function fetchFiles(itemId: string) {
  await collectionGalleryFileList(itemId, { type: props.collection.type })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        images.value[itemId] = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

// gallery
async function fetchGallery() {
  await collectionGalleryList(props.collection.collection_id, {
    full: true,
  })
    .then((response) => {
      const { success, message } = response;

      if (success) {
        galleries.value = response.data.list;

        galleries.value.forEach((item) => {
          fetchFiles(item.item_id);
        });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function addGallery() {
  showModalAdd.value = true;
}

function addGalleryCancel() {
  payloadGallery.value = null;
  showModalAdd.value = false;
}

function addGalleryConfirm() {
  if (!payloadGallery.value) return;

  showModalAdd.value = false;

  collectionGalleryCreate(props.collection.collection_id, payloadGallery.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        fetchGallery();
        editGallery(data);
        addGalleryCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      showModalAdd.value = true;
      toast({ message: t("error.default"), type: "error" });
    });
}

function editGallery(item: CollectionGalleryType) {
  selectedGallery.value = item;
  showModalEdit.value = true;
}

function editGalleryCancel() {
  selectedGallery.value = null;
  showModalEdit.value = false;
}

function editGalleryPatch(payload: Partial<CollectionGalleryType>) {
  if (!selectedGallery.value) return;

  collectionGalleryPatch(selectedGallery.value.item_id, payload)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchGallery();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      showModalEdit.value = true;
      toast({ message: t("error.default"), type: "error" });
    });
}

function delGallery(item: CollectionGalleryType) {
  selectedGallery.value = item;
  showModalDel.value = true;
}

function delGalleryCancel() {
  selectedGallery.value = null;
  showModalDel.value = false;
}

function delGalleryConfirm() {
  if (!selectedGallery.value) return;

  collectionGalleryDel(selectedGallery.value.item_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchGallery();
        delGalleryCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

// image
function addGalleryImage(item: CollectionGalleryType) {
  selectedGallery.value = item;
  showModalAddImage.value = true;
}

function addGalleryImageCancel() {
  selectedGallery.value = null;
  showModalAddImage.value = false;
}

async function addGalleryImageConfirm(file: string) {
  showModalAddImage.value = false;

  store.commit("SET_LOADING", true);

  collectionGalleryFileCreate(
    props.collection.collection_id,
    selectedGallery.value.item_id,
    file
  )
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchFiles(selectedGallery.value.item_id);
        addGalleryImageCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      showModalAdd.value = true;
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function removeGalleryImage(item: CollectionGalleryType, fileId: string) {
  selectedGallery.value = item;
  selectedImage.value = fileId;
  showModalDelImage.value = true;
}

function removeGalleryImageCancel() {
  selectedGallery.value = null;
  selectedImage.value = null;
  showModalDelImage.value = false;
}

async function removeGalleryImageConfirm() {
  showModalDelImage.value = false;

  store.commit("SET_LOADING", true);

  await collectionGalleryFileRemove(
    selectedGallery.value.item_id,
    selectedImage.value
  )
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchFiles(selectedGallery.value.item_id);
        removeGalleryImageCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function galleryImageShow(item_id: string, index: number) {
  // selectedImage.value = url;
  selectedImageList.value = images.value[item_id]?.map((item) => item.large);
  selectedImageIndex.value = index;
  showImage.value = true;
}

function galleryImageCancel() {
  selectedImage.value = null;
  selectedImageIndex.value = null;
  showImage.value = false;
}

onMounted(async () => {
  loading.value = true;
  await fetchGallery();
  loading.value = false;
});
</script>

<template>
  <section class="tab-collection">
    <div class="content">
      <div class="actions">
        <button class="btn btn-md btn-primary" @click="addGallery()">
          <svg-icon icon="add" size="1.5rem" />
          <span>
            {{ t("collection.labels.add_gallery")?.toUpperCase() }}
          </span>
        </button>
      </div>

      <div class="galleries">
        <template v-if="loading">
          <div class="empty-data">
            <base-loading />
          </div>
        </template>
        <template v-else>
          <template v-for="item in galleries" :key="item.item_id">
            <div class="gallery">
              <div class="header">
                <div class="name">
                  {{ item.name }}
                </div>
                <div class="actions">
                  <button
                    class="btn btn-transparent w-9 h-9 p-0"
                    @click="editGallery(item)"
                  >
                    <svg-icon icon="edit" size="1.5rem" />
                  </button>
                  <button
                    class="btn btn-transparent w-9 h-9 p-0"
                    @click="delGallery(item)"
                  >
                    <svg-icon icon="trash" size="1.5rem" />
                  </button>
                </div>
              </div>
              <div class="images">
                <template v-if="!images[item.item_id]?.length">
                  <div class="empty-data">
                    {{ t("collection.texts.empty_images") }}
                  </div>
                </template>
                <template v-else>
                  <div class="list">
                    <template
                      v-for="(image, imageIndex) in images[item.item_id]"
                      :key="image.id"
                    >
                      <div class="image">
                        <div class="avatar">
                          <img
                            :src="image.small"
                            @click="galleryImageShow(item.item_id, imageIndex)"
                          />
                        </div>
                        <div class="actions">
                          <button
                            class="btn btn-transparent w-9 h-9 p-0"
                            @click="removeGalleryImage(item, image.id)"
                          >
                            <svg-icon icon="trash" size="1.5rem" />
                          </button>
                        </div>
                      </div>
                    </template>
                  </div>
                </template>
                <button
                  class="btn btn-md btn-primary"
                  @click="addGalleryImage(item)"
                >
                  <svg-icon icon="add" size="1.25rem" />
                  <span>
                    {{ t("collection.labels.image")?.toUpperCase() }}
                  </span>
                </button>
              </div>
            </div>
          </template>
          <template v-if="!galleries.length">
            <div class="empty-data">
              {{ t("collection.texts.empty_gallery") }}
            </div>
          </template>
        </template>
      </div>
    </div>
  </section>

  <template v-if="showModalAddImage">
    <crop-image
      @change="addGalleryImageConfirm($event)"
      @cancel="addGalleryImageCancel()"
    />
  </template>

  <template v-if="showImage">
    <!-- <view-image :image="selectedImage" @close="galleryImageCancel" /> -->
    <view-image-list
      :images="selectedImageList"
      :selected="selectedImageIndex"
      @close="galleryImageCancel"
    />
  </template>

  <base-modal
    :show="showModalAdd"
    :title="t('collection.modals.titles.add_gallery').toUpperCase()"
    :show-button-confirm="true"
    :button-confirm="t('common.labels.save')"
    :button-close="t('common.labels.cancel')"
    @close="addGalleryCancel()"
    @confirm="addGalleryConfirm()"
  >
    <template #content>
      <form-gallery @update:data="payloadGallery = $event" />
    </template>
  </base-modal>

  <template v-if="showModalEdit && selectedGallery">
    <base-sidebar
      :width="$store.getters.mobile ? '90vw' : '60vw'"
      :open="true"
      :outside-close="true"
      @close="editGalleryCancel()"
    >
      <form-gallery
        :data="selectedGallery"
        :is-patch="true"
        @update:data="editGalleryPatch($event)"
      />
      <list-translate
        :owner-id="selectedGallery.item_id"
        :language="locale.toLowerCase()"
        :fields="translationFields"
      />
    </base-sidebar>
  </template>

  <base-confirm
    :show="showModalDel"
    :title="t('collection.modals.titles.del_gallery').toUpperCase()"
    :message="
      t('collection.modals.messages.del_gallery', {
        name: selectedGallery?.name?.toUpperCase(),
      })
    "
    @cancel="delGalleryCancel()"
    @confirm="delGalleryConfirm()"
  />

  <base-confirm
    :show="showModalDelImage"
    :title="t('collection.modals.titles.del_image').toUpperCase()"
    :message="
      t('collection.modals.messages.del_image', {
        name: selectedGallery?.name?.toUpperCase(),
      })
    "
    @cancel="removeGalleryImageCancel()"
    @confirm="removeGalleryImageConfirm()"
  />
</template>

<style lang="scss" scoped>
.file-input {
  display: none;
}

.tab-collection {
  display: flex;
  flex-direction: column;
  padding-block: 1rem;
  gap: 1rem;

  .content {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      margin-block-end: 1.5rem;
    }

    .galleries {
      display: flex;
      flex-direction: column;
      gap: 1rem;

      .gallery {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 1rem;
        padding: 1rem;
        background: var(--white-off);
        border-radius: 0.5rem;

        .header {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
          width: 100%;

          .name {
            font-size: 1.2rem;
            font-weight: 700;
            margin-left: 1rem;
          }

          .actions {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
          }
        }

        .images {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          width: 100%;
          gap: 1rem;

          .list {
            display: flex;
            flex-wrap: wrap;
            justify-content: flex-start;
            align-items: flex-start;
            width: 100%;
            gap: 1.5rem;
            row-gap: 1.5rem;
            padding: 1rem;
            background: #fff;
            border-radius: 0.25rem;

            .image {
              display: flex;
              flex-direction: column;
              gap: 0.5rem;

              .avatar {
                display: flex;
                align-items: center;
                justify-content: center;
                width: v-bind("imageSize");
                height: calc(v-bind("imageSize") / v-bind("ratio"));
                min-width: 3rem;
                min-height: 3rem;
                color: var(--text-medium);
                overflow: hidden;
              }
            }
          }
        }
      }

      .empty-data {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 1rem;
        padding-block: 2rem;
        background: var(--white-off);
        border-radius: 0.5rem;
      }
    }
  }
}

@media screen and (max-width: 1024px) {
  .tab-collection {
    .content {
      padding-inline: 0;
    }
  }
}
</style>
